import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Observes <body> class changes so slides scoped to an A/B test class
   * (added via JS at runtime) are re-evaluated as soon as the class appears.
   * @type {MutationObserver|undefined}
   */
  #bodyObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Normalize to the first visible slide (scoped slides may be hidden).
    this.current = 0;

    this.#bodyObserver = new MutationObserver(() => {
      this.current = this.#current;
    });
    this.#bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    this.#bodyObserver?.disconnect();
    this.#bodyObserver = undefined;
  }

  /**
   * Slides that are currently visible (not hidden by A/B test scoping).
   * @returns {HTMLElement[]}
   */
  get visibleSlides() {
    return (this.refs.slides ?? []).filter((slide) => slide.offsetParent !== null);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const visible = this.visibleSlides;

    // Hide every slide, then reveal only the active visible one.
    this.refs.slides?.forEach((slide) => slide.setAttribute('aria-hidden', 'true'));

    if (visible.length === 0) return;

    let relativeIndex = current % visible.length;
    if (relativeIndex < 0) {
      relativeIndex += visible.length;
    }

    visible[relativeIndex]?.setAttribute('aria-hidden', 'false');
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
